import { useEffect, useLayoutEffect, useState } from 'react';
import {
  BUSINESS_TZ,
  bubbleWords,
  localDateIso,
  notificationGroup,
  stackFit,
  stackList,
  waitingLabel,
  type DeskNotification,
} from '@bbbk/schema';
import { Avatar, Icon } from '../common';
import { useClickOutside } from '../../lib/useClickOutside';
import { useMediaQuery, PHONE_QUERY } from '../../lib/useMediaQuery';
import { useDeskNotifications } from './DeskNotifications';

// notifications-daily-stack — WHAT IS WAITING ON PATRICIA, beside the word "Daily" (flow 6.1).
//
// Inside the 40px title row, so it costs no height (AC1). On a desk, as many bubbles side by side as
// the row holds at 250px each, the rest in a "+N" deck that fans out over the page (AC2, AC4); on a
// phone, one deck (AC3). A bubble stays until its job is resolved — opening it is not doing it
// (decision `notifications-bubble-stays-until-resolved`). Nothing waiting draws nothing (AC9).

type Group = ReturnType<typeof notificationGroup>;
const FILTERS: Group[] = ['Requests', 'Screenshots', 'Cards'];
/** More than this many rows in the fan earns filter chips (AC4a). */
const CHIPS_AFTER = 6;

const whoOf = (e: DeskNotification) => e.sender ?? e.household ?? 'A customer';
/** One person: two households can share a first name, and their rows must never share a face. */
const personOf = (e: DeskNotification) => `${e.ownerId ?? ''}|${whoOf(e)}`;

/**
 * THE APP'S OWN AVATAR (Freddi, 2026-10-14: "update the avatars so they match the rest of the app"):
 * the sender's full name and the id App access draws them by, so Maeve wears the same initials and
 * tone here as on her household's App access row. An unknown sender falls back to the household.
 */
function Face({ e, size }: { e: DeskNotification; size: number }) {
  const name = e.senderName ?? e.household ?? whoOf(e);
  const id = e.senderContactId != null ? `c${e.senderContactId}` : (e.ownerId ?? name);
  return <span className="ds-face" aria-hidden="true"><Avatar name={name} id={id} size={size} /></span>;
}

/**
 * WHAT KIND IT IS, AT A GLANCE (Freddi, 2026-10-14: "the notifications should have icons etc so
 * theyre easily identifyable at a glance"). One icon and one tint per kind, on every bubble, fan row
 * and phone card — the words still say it; the icon lets her scan a row without reading it.
 */
// The booking modal's own glyphs for the same subjects: RequestCard's `requested`, SelfServiceCard's money and vax, AccessColumn's person.
const KIND_ICON: Record<DeskNotification['kind'], string> = { request: 'clipboard-check', screenshot: 'euro', card: 'syringe', registration: 'user' };
export function KindIcon({ kind }: { kind: DeskNotification['kind'] }) {
  return <span className={`ds-kind ds-kind-${kind}`} aria-hidden="true"><Icon name={KIND_ICON[kind]} size={15} /></span>;
}

const opensLabel = (e: DeskNotification) => (e.bookingId ? 'Open the booking.' : 'Open the household.');

function When({ e, now }: { e: DeskNotification; now: number }) {
  const w = waitingLabel(e.createdAt, now);
  return w.aged ? <em className="ds-aged">{w.text}</em> : <time dateTime={e.createdAt}>{w.text}</time>;
}

/** `Today`, `Yesterday`, or the weekday — the fan's day headings, in Dublin's calendar. */
function dayHeading(createdAt: string, now: number): string {
  const day = localDateIso(new Date(createdAt), BUSINESS_TZ);
  if (day === localDateIso(new Date(now), BUSINESS_TZ)) return 'Today';
  if (day === localDateIso(new Date(now - 86_400_000), BUSINESS_TZ)) return 'Yesterday';
  return new Intl.DateTimeFormat('en-IE', { timeZone: BUSINESS_TZ, weekday: 'long' }).format(new Date(createdAt));
}

function Fan({ list, head, filter, now, onOpen, onFilter }: {
  list: DeskNotification[]; head: string; filter: Group | null; now: number;
  onOpen: (e: DeskNotification) => void; onFilter: (g: Group | null) => void;
}) {
  const long = list.length > CHIPS_AFTER;
  const counts = new Map<Group, number>();
  for (const e of list) counts.set(notificationGroup(e.kind), (counts.get(notificationGroup(e.kind)) ?? 0) + 1);
  const shown = filter ? list.filter((e) => notificationGroup(e.kind) === filter) : list;
  const days = new Set(shown.map((e) => dayHeading(e.createdAt, now)));
  let lastDay: string | null = null;
  return (
    <div className="ds-fan" role="dialog" aria-label="Waiting on you">
      <div className="ds-fan-head"><span>{head}</span></div>
      {long && (
        <div className="ds-fan-chips" role="group" aria-label="Show">
          <button type="button" className={filter ? '' : 'ds-on'} onClick={() => onFilter(null)}>All {list.length}</button>
          {FILTERS.filter((g) => counts.get(g)).map((g) => (
            <button key={g} type="button" className={filter === g ? 'ds-on' : ''} onClick={() => onFilter(g)}>{g} {counts.get(g)}</button>
          ))}
        </div>
      )}
      <div className="ds-fan-scroll">
        {shown.map((e, i) => {
          const day = dayHeading(e.createdAt, now);
          const heading = days.size > 1 && day !== lastDay ? day : null;
          lastDay = day;
          const next = shown[i + 1];
          const lastOf = !next || personOf(next) !== personOf(e) || dayHeading(next.createdAt, now) !== day;
          const w = bubbleWords(e);
          return (
            <div key={e.id} className="ds-fan-row">
              {heading && <div className="ds-fan-day">{heading}</div>}
              <div className={'ds-msg' + (lastOf ? ' ds-lastof' : '')} data-id={e.id}>
                <Face e={e} size={26} />
                <button type="button" className="ds-bub" aria-label={`${w.title}, ${w.detail}. ${opensLabel(e)}`} onClick={() => onOpen(e)}>
                  <KindIcon kind={e.kind} />
                  <span className="ds-t"><b>{w.title}</b><span>{w.detail}</span></span>
                  <When e={e} now={now} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export interface DailyStackViewProps {
  events: readonly DeskNotification[];
  /** The width the bubbles get: the title row less the word "Daily", measured, never guessed. */
  rowWidth: number;
  phone: boolean;
  fanned: boolean;
  filter: Group | null;
  now: number;
  landingId: string | null;
  onOpen: (e: DeskNotification) => void;
  onToggleFan: () => void;
  onFilter: (g: Group | null) => void;
}

export function DailyStackView({ events, rowWidth, phone, fanned, filter, now, landingId, onOpen, onToggleFan, onFilter }: DailyStackViewProps) {
  const list = stackList(events);
  if (!list.length) return null;

  if (phone) {
    const front = list[0]!;
    const w = bubbleWords(front);
    const behind = Math.min(list.length - 1, 2);
    return (
      <div className="ds-stack ds-phone">
        <button type="button" className="ds-deck" aria-expanded={fanned}
          aria-label={`${list.length} waiting on you. Newest: ${w.title}. Open the list.`} onClick={onToggleFan}>
          <Face e={front} size={26} />
          <span className="ds-cards">
            {behind >= 2 && <span className="ds-card ds-l2" aria-hidden="true" />}
            {behind >= 1 && <span className="ds-card ds-l1" aria-hidden="true" />}
            <span className={'ds-card ds-l0' + (landingId === front.id ? ' ds-land' : '')}>
              <KindIcon kind={front.kind} />
              <span className="ds-txt"><b>{whoOf(front)}</b> {w.title.slice(whoOf(front).length + 1)}</span>
              <span className="ds-verb">{w.verb}</span>
            </span>
          </span>
          {list.length > 1 && <span className="ds-count">{list.length}</span>}
        </button>
        {fanned && <Fan list={list} head={`Waiting on you · ${list.length}`} filter={filter} now={now} onOpen={onOpen} onFilter={onFilter} />}
      </div>
    );
  }

  const k = stackFit(rowWidth, list.length);
  const rest = list.slice(k);
  return (
    <div className="ds-stack">
      <div className="ds-lane">
        {list.slice(0, k).map((e) => {
          const w = bubbleWords(e);
          return (
            <button key={e.id} type="button" className={'ds-item' + (landingId === e.id ? ' ds-land' : '')} data-id={e.id}
              aria-label={`${w.title}, ${w.detail}. ${opensLabel(e)}`} onClick={() => onOpen(e)}>
              <Face e={e} size={24} />
              <span className="ds-chip">
                <KindIcon kind={e.kind} />
                <span className="ds-t"><b>{w.title}</b><span>{w.detail} · <When e={e} now={now} /></span></span>
                <span className="ds-verb">{w.verb}</span>
              </span>
            </button>
          );
        })}
        {rest.length > 0 && (
          <button type="button" className="ds-more" aria-expanded={fanned} aria-label={`${rest.length} more waiting on you. Open the list.`} onClick={onToggleFan}>
            <span className="ds-pk ds-b2" aria-hidden="true" />
            <span className="ds-pk ds-b1" aria-hidden="true" />
            <span className="ds-pk ds-b0"><i>+{rest.length > 99 ? 99 : rest.length}</i> more</span>
          </button>
        )}
      </div>
      {fanned && rest.length > 0 && <Fan list={rest} head={`${rest.length} more waiting on you`} filter={filter} now={now} onOpen={onOpen} onFilter={onFilter} />}
    </div>
  );
}

/** The live stack: the shell's one read, the row measured as it resizes, the fan's own state. */
export function DailyStack() {
  const desk = useDeskNotifications();
  const phone = useMediaQuery(PHONE_QUERY);
  const [fanned, setFanned] = useState(false);
  const [filter, setFilter] = useState<Group | null>(null);
  const [rowWidth, setRowWidth] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  // One element: the fan closes on a click outside it, and its width is what the bubbles get.
  const ref = useClickOutside<HTMLDivElement>(() => setFanned(false));

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setRowWidth(el.clientWidth);
    read();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(read) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [ref]);
  // The "waiting N days" labels move on with the clock, not only with a refresh.
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 60_000); return () => window.clearInterval(t); }, []);
  useEffect(() => { setNow(Date.now()); }, [desk?.events]);

  if (!desk) return null;
  return (
    <div className="ds-host" ref={ref}>
      <DailyStackView
        events={desk.events}
        rowWidth={rowWidth}
        phone={phone}
        fanned={fanned}
        filter={filter}
        now={now}
        landingId={desk.landingId}
        onOpen={(e) => { setFanned(false); desk.open(e); }}
        onToggleFan={() => setFanned((f) => !f)}
        onFilter={setFilter}
      />
    </div>
  );
}
